<template>
    <div class="game-card" :class="{ 'game-finished': game.is_finished, 'game-upcoming': !game.is_finished }">
        <div class="game-header">
            <div class="game-date-time">
                <span class="game-date">{{ game.datetime }}</span>
                <span v-if="game.stage_name" class="game-stage">{{ game.stage_name }}</span>
                <span v-if="game.tour_number" class="game-tour">Тур {{ game.tour_number }}</span>
            </div>
            <div v-if="game.venue" class="game-venue">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                        d="M8 0C4.1 0 1 3.1 1 7c0 5.4 7 9 7 9s7-3.6 7-9c0-3.9-3.1-7-7-7zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5S6.6 4.5 8 4.5s2.5 1.1 2.5 2.5S9.4 9.5 8 9.5z"
                        fill="currentColor" />
                </svg>
                {{ game.venue }}
            </div>
        </div>

        <div class="game-teams">
            <component
                :is="teamPublicPath(game.home_team) ? 'a' : 'div'"
                :href="teamPublicPath(game.home_team) || undefined"
                class="game-team"
                :class="{
                    'team-winner': game.winner === 'home',
                    'game-team--link': !!teamPublicPath(game.home_team),
                }"
            >
                <div class="team-logo" v-if="game.home_team.logo_path">
                    <img :src="game.home_team.logo_path" :alt="game.home_team.name" />
                </div>
                <div class="team-logo placeholder" v-else>
                    {{ game.home_team.name?.charAt(0) }}
                </div>
                <div class="team-info">
                    <span class="team-name">{{ game.home_team.name }}</span>
                </div>
                <div v-if="game.is_finished" class="team-score">
                    {{ game.home_score }}
                </div>
            </component>

            <div class="game-vs">
                <span v-if="!game.is_finished">VS</span>
                <span v-else class="score-separator">:</span>
            </div>

            <component
                :is="teamPublicPath(game.away_team) ? 'a' : 'div'"
                :href="teamPublicPath(game.away_team) || undefined"
                class="game-team"
                :class="{
                    'team-winner': game.winner === 'away',
                    'game-team--link': !!teamPublicPath(game.away_team),
                }"
            >
                <div class="team-logo" v-if="game.away_team.logo_path">
                    <img :src="game.away_team.logo_path" :alt="game.away_team.name" />
                </div>
                <div class="team-logo placeholder" v-else>
                    {{ game.away_team.name?.charAt(0) }}
                </div>
                <div class="team-info">
                    <span class="team-name">{{ game.away_team.name }}</span>
                </div>
                <div v-if="game.is_finished" class="team-score">
                    {{ game.away_score }}
                </div>
            </component>
        </div>

        <div v-if="game.is_finished && formattedSets" class="game-sets">
            <span class="sets-label">Сеты:</span>
            <span class="sets-score">{{ formattedSets }}</span>
        </div>

        <div v-if="game.is_finished" class="game-footer">
            <div v-if="game.attendance" class="game-meta">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M8 8a3 3 0 100-6 3 3 0 000 6zm0 1c-2.67 0-8 1.34-8 4v1h16v-1c0-2.66-5.33-4-8-4z"
                        fill="currentColor" />
                </svg>
                {{ game.attendance }} зрителей
            </div>
            <div v-if="game.referee" class="game-meta">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M8 0L0 3v5c0 4.4 3.5 8.4 8 9 4.5-.6 8-4.6 8-9V3L8 0z" fill="currentColor" />
                </svg>
                {{ game.referee }}
            </div>
            <a v-if="game.match_video_url" :href="game.match_video_url" target="_blank" class="game-video-link">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm4 8l-5-3v6l5-3z" fill="currentColor" />
                </svg>
                Видео
            </a>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { Link } from '@inertiajs/vue3';
import { teamPublicPath } from '@/utils/teamUrl';

const props = defineProps({
    game: {
        type: Object,
        required: true
    }
});

const stageNames = {
    championship: 'Чемпионат',
    cup: 'Кубок',
    friendly: 'Товарищеский',
    playoff: 'Плей-офф',
    group_stage: 'Групповой этап',
    quarterfinal: 'Четвертьфинал',
    semifinal: 'Полуфинал',
    final: 'Финал',
};

const formattedSets = computed(() => {
    if (!props.game.sets_score || !Array.isArray(props.game.sets_score)) {
        return null;
    }
    return props.game.sets_score.map(set => `${set.home || 0}:${set.away || 0}`).join(', ');
});
</script>
